import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import ArticleCategoryMultiDropdown from "components/dropdowns/multi/ArticleCategoryMultiDropdown";
import RelatedArticleMultiDropdown from "components/dropdowns/multi/RelatedArticleMultiDropdown";
import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";
import moment from "moment";
import React, { useRef } from "react";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { useValidate, useValidators } from "formvalidation/hooks";
import ValidatorContext from "formvalidation/FormContext";
import { InputBase } from "components/Atoms/formInputs/InputBase";
import { useMutation } from "@tanstack/react-query";
import { createStringInSlugFormat } from "CMS/utils";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { HtmlEditor } from "components/ValidatedFields/HtmlEditor";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";

interface Props {
  article: server.dto.Article;
  existingTranslation: server.dto.ArticleTranslation;
  language: server.dto.Languages;
  needsSaving: () => void;
  onCancel: () => void;
  onFinishedSaving: () => void;
}

const EditArticle = (props: Props) => {
  const [internalTitle, setInternalTitle] = React.useState(props.article.internalTitle ?? "");
  const [publishDate, setPublishDate] = React.useState(() => {
    if (props.existingTranslation) {
      return moment.utc(props.article.publication);
    }
    return moment.utc();
  });
  const [isPublished, setIsPublished] = React.useState(props.article.isPublished);
  const [title, setTitle] = React.useState(props.existingTranslation?.title ?? "");
  const [slug, setSlug] = React.useState(props.existingTranslation?.slug ?? "");
  const [categories, setCategories] = React.useState<server.dto.ArticleCategory[]>(props.article.categories ?? []);
  const [relevantArticles, setRelevantArticles] = React.useState<server.dto.RelatedArticle[]>(props.article.relatedArticles);
  const [summary, setSummary] = React.useState(props.existingTranslation?.summary ?? "");
  const [content, setContent] = React.useState(props.existingTranslation?.content ?? "");

  const saveMutation = useMutation(window.CoreApi.Article.Save);

  const onSave = async () => {
    const validationResult = await validators.validateAll();

    if (validationResult.hasErrors) {
      return;
    }

    const res = await saveMutation.mutateAsync({
      articleCategoryIds: categories.map(c => c.articleCategoryId),
      articleId: props.article.articleId,
      content,
      internalTitle,
      isPublished,
      publication: publishDate.toDate(),
      language: props.language,
      relevantArticleIds: relevantArticles.map(r => r.articleId),
      slug,
      summary,
      title
    });

    if (res.data.hasErrors) {
      window.displayGenericModal("Error", res.data.errors.map(e => e.message).join(","), "mini");
      return;
    }

    props.onFinishedSaving();
  }

  const onCancel = () => {
    props.onCancel();
  }

  const validators = useValidators();

  return <ValidatorContext settings={validators.validatorSettings}>
    <div className="flex-column card-surface-standalone gap padding-big grow1">
      <div className="flex gap">
        <div className="grow1">
          <LabelledFormElement label="Internal title" mandatory>
            <RequiredTextInput value={internalTitle} onChange={setInternalTitle} validatorKey="internalTitle" maxLength={255} />
          </LabelledFormElement>
        </div>
        <div>
          <LabelledFormElement label="Publish date" mandatory>
            <FilterDatePicker
              minDate={moment.utc().subtract(10, "year")}
              maxDate={moment.utc().add(10, "year")}
              value={publishDate}
              onChange={setPublishDate}
            />
          </LabelledFormElement>
        </div>
        <div>
          <LabelledFormElement label="Published">
            <Checkbox checked={isPublished} onChange={(_, checked) => setIsPublished(checked)} />
          </LabelledFormElement>
        </div>
        <div>
          <ButtonPrimary disabled={saveMutation.isLoading} loading={saveMutation.isLoading} label={RESX.Buttons.btnSave} onClick={onSave} htmlType="submit" />
        </div>
        <div>
          <ButtonSecondary disabled={saveMutation.isLoading} label={RESX.Buttons.btnCancel} onClick={onCancel} />
        </div>
      </div>

      <div className="flex gap">
        <div className="grow1">
          <LabelledFormElement label="Title" mandatory>
            <RequiredTextInput value={title} onChange={setTitle} validatorKey="title" maxLength={255} />
          </LabelledFormElement>
        </div>
        <div className="grow1">
          <LabelledFormElement label="Slug" mandatory>
            <SlugInput value={slug} onChange={setSlug} validatorKey="slug" />
          </LabelledFormElement>
        </div>
      </div>

      <div className="flex">
        <div className="grow1">
          <LabelledFormElement label="Categories">
            <ArticleCategoryMultiDropdown section={props.article.section} value={categories} onChange={setCategories} />
          </LabelledFormElement>
        </div>
      </div>

      <div className="flex">
        <div className="grow1">
          <LabelledFormElement label="Related articles">
            <RelatedArticleMultiDropdown section={props.article.section} value={relevantArticles} onChange={setRelevantArticles} />
          </LabelledFormElement>
        </div>
      </div>

      <div>
        <OptionalTextAreaInput
          value={summary}
          onChange={v => {
            setSummary(v);
            props.needsSaving();
          } }
          rows={4}
          maxLength={9999999}
        />
      </div>

      <HtmlEditor
        toolbar="CMS"
        maxContentLength={9999999}
        content={content}
        onChange={setContent}
      />
    </div>
  </ValidatorContext>
}

interface LabelledFormElementProps {
  label: string;
  mandatory?: boolean;
}
const LabelledFormElement = (props: React.PropsWithChildren<LabelledFormElementProps>) => {
  return <>
    <label style={{ display: "block" }} htmlFor={props.label}>{props.label}&nbsp;{props.mandatory && "*"}</label>
    {props.children}
  </>
}

interface SlugInputProps {
  value: string
  onChange: (newTitle: string) => void
  validatorKey: string
}

const SlugInput = (props: SlugInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = (input: string) => {
    const hasValue = input.trim().length > 0;
    if (!hasValue) return hasValue;

    const isValidSlug = alphaNumericAndDashes(input);
    return isValidSlug;
  };

  const validation = useValidate({
    key: props.validatorKey,
    title: "Slug",
    value: props.value,
    validate: async (value) => alphaNumericAndDashes(value),
    ref: inputRef
  })

  return <InputBase
    ref={inputRef}
    value={props.value}
    onChange={props.onChange}
    maxLength={255}
    error={validation.error.hasError}
    onBlur={() => {
      const newValue = createStringInSlugFormat(props.value);
      validate(newValue);
      props.onChange(newValue);
    }}
  />;
};

const alphaNumericAndDashes = (value: string): boolean => {
  let valid;

  if (value.trim() === "") {
    valid = false;
  } else {
    const regexp = /^[a-zA-Z0-9-]+$/;
    valid = regexp.test(value);
  }

  return valid;
};

export default EditArticle;